

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from "ag-grid-react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import MDButton from 'components/MDButton';
// icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
// utils and helpers
import { debounce } from "utilities/common";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../../redux/slices/modalSlice';
import { deleteRegion, getRegions, getUnassignedDrivers, unassignDrivers, unassignSubRegion } from "../../../../redux/slices/hyperwolf/settings";
// custom components
import Search from 'examples/Search';
import StatusText from 'common/component/StatusText';
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import { formatTimestampToWithoutTime } from "utilities/common";

export default function Regions({ canView, canCreate, canEdit, canDelete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isUnassignDriversLoading = useSelector((state) => state.hyperwolfSettings?.isUnassignDriversLoading);

  const [searchTerm, setSearchTerm] = useState('');
  const [customFilters, setCustomFilters] = useState({});
  const [regionsList, setRegionsList] = useState({});
  const [expanded, setExpanded] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const subRegionColumnDefs = [
    {
      headerName: 'Sub Regions',
      field: 'name',
      suppressMenu: false,
      minWidth: 300,
      sortable: false
    },
    {
      headerName: 'Assigned Drivers Today',
      field: 'driver',
      suppressMenu: false,
      sortable: false,
      minWidth: 200,
      valueGetter: ({ data }) => data?.driver || "-"
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      sortable: false,
      minWidth: 100,
      cellRenderer: (props) => {
        return <StatusText status={props?.data?.status ? "Active" : "Inactive"} />;
      },
    },
    {
      headerName: 'Actions',
      cellRenderer: (props) => {
        return <Box display='inline-flex'>
          <IconButton
            disabled={!canEdit}
            onClick={() => dispatch(openCustomModalDialog({
              modalData: {
                currentDetails: props.data,
                drivers: drivers,
                handleRegionsList: handleRegionsList,
                regionId: expanded,
                handleDriversList
              },
              modal: 'UPDATE_ASSIGNED_SUBREGION'
            }))}
            // onClick={() => navigate(`/regions/update/${props?.data.regionId}`)}
            color='primary' size='small' variant='primary'
          >
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            disabled={!canDelete}
            onClick={() => dispatch(openCustomModalDialog({
              modalData: {
                deleteHandler: handleDeleteSubRegion,
                deleteId: { subRegionId: props.data?.subRegionId, regionId: expanded },
                deleteType: "Sub Region"
              },
              modal: 'DELETE_MODAL'
            }))}
            color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}
          >
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </Box>;
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      maxWidth: 100,
      // pinned: 'right'
    },
  ];

  const handleDriversList = async () => {
    try {
      const response = await dispatch(getUnassignedDrivers({ limit: 200 })).unwrap();
      if (response?.data) {
        setDrivers(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  const handleExpandRegion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : "");
  };

  const handleRegionsList = async (filters = {}) => {
    setIsLoading(true)
    try {
      const response = await dispatch(getRegions({ ...filters, limit: 100 })).unwrap()
      if (response?.data) {
        setRegionsList(response)
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteSubRegion = async (data) => {
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(unassignSubRegion(data)).unwrap();

      showToastMsg(res?.message || "Sucess", "success")
      dispatch(closeCustomModalDialog());
      handleRegionsList();
      handleDriversList();
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err, "error")
    };
  };

  const handleDeleteRegion = async (regionId) => {
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(deleteRegion(regionId)).unwrap();
      showToastMsg(res?.message || "Success", "success")
      dispatch(closeCustomModalDialog());
      handleRegionsList();
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err, "error")
    };
  };

  const handleUnassignDrivers = async () => {
    try {
      const res = await dispatch(unassignDrivers()).unwrap();
      showToastMsg(res?.message || res?.data?.message || "Drivers unassigned successfully", "success");
      handleRegionsList();
      handleDriversList();
    } catch (err) {
      showToastMsg(err, "error");
    }
  };

  const searchHandler = useCallback(debounce((event) => {
    const filters = { ...customFilters, search: event.target.value };
    setCustomFilters({ ...customFilters, search: event.target.value });
    handleRegionsList(filters);
  }, 1000), []);

  useEffect(() => {
    handleRegionsList();
    handleDriversList();
  }, []);

  const RegionRow = ({ region }) => {
    return (
      <Box sx={{ width: "100%" }} className="accordion-header">
        <Typography className="field-region">
          {region?.region ? region?.region : "-"}
        </Typography>
        <Typography className="field-opening-hour">
          {formatTimestampToWithoutTime(region?.createdDate)}
        </Typography>
        <Typography className="field-status">
          <StatusText status={region?.status ? "Active" : "Inactive"} />
        </Typography>
        <Box display="flex" flexDirection="row" gap={0.5} className="field-actions">
          <IconButton onClick={() => navigate(`/regions/view/${region.regionId}`)} color='primary' size='small' variant='primary'>
            <VisibilityIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            color='primary' size='small' variant='primary'
            onClick={() => navigate(`/regions/update/${region.regionId}`)}
          // onClick={() => dispatch(openCustomModalDialog({ modalData: { regionId: region.regionId, data: region, handleRegionsList }, modal: 'ADD_REGION' }))}
          >
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            color='error'
            size='small'
            variant='primary'
            onClick={() => dispatch(openCustomModalDialog({ modalData: { deleteHandler: handleDeleteRegion, deleteId: region.regionId }, modal: 'DELETE_MODAL' }))}
          >
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="cus-four-columns region-four-columns">
        <Grid container alignItems='center' rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4'>Regions</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack direction='row' justifyContent={{ xs: 'start', sm: 'end' }} flexWrap='wrap' alignItems='stretch' gap={2}>
              <Search
                placeholder='Search'
                label=''
                value={searchTerm}
                onChange={event => {
                  const { value } = event.target;
                  setSearchTerm(value);
                  searchHandler(event)
                }}
                style={{ minWidth: "300px" }}
              />
              <MDButton
                startIcon={<AddRoundedIcon fontSize='large' />}
                variant='contained' color='primary'
                onClick={() => navigate('/regions/add')}
              // onClick={() => dispatch(openCustomModalDialog({ modalData: { handleRegionsList }, modal: 'ADD_REGION' }))}
              >
                Add Region
              </MDButton>
              <MDButton
                // startIcon={<AddRoundedIcon fontSize='large' />}
                variant='contained' color='primary'
                onClick={() => handleUnassignDrivers()}
                loading={isUnassignDriversLoading}
              // onClick={() => dispatch(openCustomModalDialog({ modalData: { handleRegionsList }, modal: 'ADD_REGION' }))}
              >
                Bulk Unassign Driver
              </MDButton>
            </Stack>
          </Grid>
        </Grid>

        <Grid container alignItems='center' className="table-with-accordion" style={{ boxShadow: "none" }}>
          <Grid item xs={12} sm={12}>
            <div className="cus-table-header">
              <Typography className="field-region">
                Region
              </Typography>
              <Typography className="field-opening-hour">
                Created At
              </Typography>
              <Typography className="field-status">
                Status
              </Typography>
              <Typography className="field-actions">
                Actions
              </Typography>
            </div>
            <div className="ag-theme-quartz" style={{ minHeight: "500px", height: "100%", width: "100%", overflow: "auto" }}>
              {
                isLoading ?
                  <div className="table-nodata-size" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center", margin: "auto" }}><TableLoader /></div>
                  : regionsList?.data?.length > 0
                    ? regionsList?.data?.map((region, index) => {
                      const subRegionsMaxHeight = Math.min((region?.subRegions?.length + 1) * 56, 300)
                      return (
                        !region?.subRegions || region.subRegions.length === 0
                          ? <Accordion className="accordion-container" key={index} expanded={expanded === region.regionId} onChange={handleExpandRegion(region.regionId)}>
                            <AccordionSummary
                              expandIcon={<span style={{ minWidth: "14px", minHeight: "1px" }} />}
                              aria-controls={`${region.regionId}-heading-content`}
                              id={`panelheading-${region.regionId}`}
                            >
                              <RegionRow region={region} />
                            </AccordionSummary>
                          </Accordion>
                          : <Accordion className="accordion-container" key={index} expanded={expanded === region.regionId} onChange={handleExpandRegion(region.regionId)}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={`${region.regionId}-heading-content`}
                              id={`panelheading-${region.regionId}`}
                            >
                              <RegionRow region={region} />
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box className="table-border-gray">
                                {
                                  !region?.subRegions || region.subRegions.length === 0
                                    ? <NoDataFound />
                                    : <div style={{ height: subRegionsMaxHeight }}>
                                      <AgGridReact
                                        className="ag-grid--table"
                                        pagination={false}
                                        rowData={region?.subRegions}
                                        noRowsOverlayComponent={NoDataFound}
                                        overlayLoadingTemplate={'<span class="table-loader"></span>'}
                                        columnDefs={subRegionColumnDefs}
                                        rowModelType={"clientSide"}
                                        headerHeight={36}
                                        rowHeight={56}
                                        rowSelection="multiple"
                                        suppressRowClickSelection={true}
                                        defaultColDef={{ ...defaultColDef }}
                                      />
                                    </div>
                                }
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                      )
                    })
                    : regionsList?.data?.length === 0
                      ? <div className="table-nodata-size" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center", margin: "auto" }}>
                        <NoDataFound />
                      </div>
                      : null
              }
            </div>
          </Grid>
        </Grid>
      </Box >
    </>
  );
}